import { observer } from 'mobx-react-lite';
import { StorefrontIcon } from '@phosphor-icons/react';
import { Navigate, Outlet, useLocation, useOutletContext } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { can } from '@/lib/capabilities';
import { hasFeature, isRetailMode, type BusinessFeature } from '@/lib/business-profile';
import { onlineOnlyPath } from '@/lib/sales-channels';
import {
  isRestaurantType,
  landingRouteFor,
  satisfiesScope,
  scopeOf,
  setupPathFor,
  type ScopeLevel,
  type WorkstationScope,
} from './routing';
import { useAuthStore } from './store-context';

/**
 * Route guards (observer components) for the router's guard seams. UX only —
 * server membership/permission checks remain authoritative
 * (01-architecture.md §3).
 *
 * V2-1: the single all-or-nothing RequireSetup became a ladder, because the
 * three route groups do not need the same thing. The office needs an
 * organization, the restaurant floor needs a branch, and only selling needs a
 * terminal. Gating every one of them on a full workstation is what forced an
 * owner to nominate a till before they could read their own orders.
 *
 *   { element: <RequireAuth/>, children: [
 *     { element: <RequireOrganization/>, children: [
 *       office routes,
 *       { path: '/pos', children: [
 *         { element: <RequireBranch/>, children: [ floor, queue, menu,
 *           { element: <RequireTerminal/>, children: [ register, today ] } ] } ] } ] } ] }
 */

/**
 * Held while the persisted session is being read. Deliberately silent: boot is
 * synchronous, so this frame is not meant to be seen — it exists so that a
 * guard can never answer "not signed in" before it has looked.
 */
function Booting() {
  return <div data-slot="auth-booting" aria-busy="true" className="min-h-dvh" />;
}

export const RequireAuth = observer(function RequireAuth() {
  const auth = useAuthStore();
  const location = useLocation();
  if (!auth.hydrated) return <Booting />;
  if (!auth.isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <Outlet />;
});

/**
 * Platform billing is global, not an organization membership capability.
 * This prevents accidental navigation only; sg-sites PlatformAdminMiddleware
 * is the security boundary for every request.
 */
export const RequirePlatformAdmin = observer(function RequirePlatformAdmin() {
  const auth = useAuthStore();
  if (!auth.hydrated) return <Booting />;
  if (!auth.isAuthenticated) return <Navigate to="/login" replace />;
  if (!auth.isPlatformAdmin) return <Navigate to="/" replace />;
  return <Outlet />;
});

/**
 * Sends the user to /setup for exactly the level this branch of the tree
 * needs, carrying where they were headed so setup returns them to it.
 */
function requireScope(level: ScopeLevel, displayName: string) {
  const Guard = observer(function Guard() {
    const auth = useAuthStore();
    const location = useLocation();
    if (!auth.hydrated) return <Booting />;
    if (!satisfiesScope(scopeOf(auth), level)) {
      return <Navigate to={setupPathFor(location.pathname + location.search)} replace />;
    }
    return <Outlet />;
  });
  Guard.displayName = displayName;
  return Guard;
}

/** Tenant scope only — every office route, and the device screen. */
export const RequireOrganization = observer(function RequireOrganization() {
  const auth = useAuthStore();
  const location = useLocation();
  if (!auth.hydrated) return <Booting />;
  if (auth.organizations.length === 0) {
    return <Navigate to="/signup/application" replace />;
  }
  if (!satisfiesScope(scopeOf(auth), 'organization')) {
    return <Navigate to={setupPathFor(location.pathname + location.search)} replace />;
  }
  return <Outlet />;
});

/**
 * Replaces the route content as one unit when the selected store cannot run.
 * It is mounted inside the office/POS shell, so navigation and account controls
 * stay visible while none of the route's cards or requests remain on screen.
 */
export const RequireOrganizationAccess = observer(function RequireOrganizationAccess() {
  const auth = useAuthStore();
  const location = useLocation();
  const outletContext = useOutletContext();
  const issue = auth.organizationAccessIssue;
  if (!issue && auth.organization?.salesControls?.online_store_enabled === false && onlineOnlyPath(location.pathname)) {
    return <Navigate to={landingRouteFor(scopeOf(auth))} replace />;
  }
  if (!issue) return <Outlet context={outletContext} />;

  const suspended = issue === 'suspended';
  return (
    <div className="grid min-h-[calc(100dvh-8rem)] place-items-center p-6">
      <section className="w-full max-w-xl rounded-card border border-border bg-card p-8 text-center shadow-card">
        <span className="mx-auto grid size-14 place-items-center rounded-full bg-warn-tint text-warn-tint-foreground">
          <StorefrontIcon size={28} weight="duotone" aria-hidden />
        </span>
        <h1 className="mt-5 text-title font-strong">
          {suspended ? 'Store suspended' : 'Store unavailable'}
        </h1>
        <p className="mx-auto mt-3 max-w-md text-body text-muted">
          {suspended
            ? 'This store has been suspended. Please contact the platform administrator to restore access.'
            : 'This store is no longer available. Please contact the platform administrator for assistance.'}
        </p>
        <Button className="mt-6" variant="outline" onClick={() => window.location.reload()}>
          Check again
        </Button>
      </section>
    </div>
  );
});
/** A physical location — the floor, the queue and the menu are branch work. */
export const RequireBranch = requireScope('branch', 'RequireBranch');
/** A nominated till — selling and the day session that owns the money. */
export const RequireTerminal = requireScope('terminal', 'RequireTerminal');

/**
 * Pre-V2-1 name for the full workstation gate. Kept as an alias so existing
 * callers/tests keep compiling; new routes should name the level they need.
 */
export const RequireSetup = RequireTerminal;

/**
 * Turn away anyone whose landing is elsewhere, without asking for a workstation
 * on the way.
 *
 * Guards that redirect to /setup answer "you need more scope". These two answer
 * "this is not your screen", so they must sit ABOVE RequireBranch/RequireTerminal
 * in the tree: a waiter typing /pos should be moved to their own landing
 * immediately, not marched through terminal setup first to arrive at a register
 * the server will refuse them anyway.
 */
function requireEligibility(allows: (scope: WorkstationScope) => boolean, displayName: string) {
  const Guard = observer(function Guard() {
    const auth = useAuthStore();
    if (!auth.hydrated) return <Booting />;
    const scope = scopeOf(auth);
    if (!allows(scope)) return <Navigate to={landingRouteFor(scope)} replace />;
    return <Outlet />;
  });
  Guard.displayName = displayName;
  return Guard;
}

/**
 * Tables exist only in a restaurant.
 *
 * Hiding the nav entry was never enough — /pos/tables and the office /tables
 * were still registered for every organization type and rendered fine when
 * typed into the address bar. The organization's VERIFIED type decides, and
 * nothing else does: a waiter role is not evidence of a restaurant, and an
 * absent type is not either.
 */
export const RequireRestaurant = requireEligibility(
  (scope) => isRestaurantType(scope.organizationType),
  'RequireRestaurant',
);

/**
 * The same lesson, pointed the other way: a screen a RESTAURANT must not reach.
 *
 * `RequireRestaurant` above closed the leak in one direction only, so the
 * parcel-model screens stayed registered for every organization type. A
 * restaurant manager could build shipping zones with real prices on them and
 * nothing would ever charge one — restaurant delivery is priced by area
 * (`deliveryPricingFor` answers 'area', src/types/checkout.ts), and no
 * restaurant surface reads a zone. Money configured into a screen that has no
 * consumer is worse than a 404, because it reports success.
 *
 * Written over `hasFeature` rather than `!isRestaurantType` deliberately. The
 * answer for these two rows is retail AND neutral
 * (src/lib/business-profile.ts:160-162), and stating that as a negation here
 * would be a second copy of the table that the negation is free to drift from —
 * exactly the six-spellings
 * failure business-profile.ts:11-22 was written to end. Ask the table.
 */
function requireFeature(feature: BusinessFeature, displayName: string) {
  return requireEligibility((scope) => hasFeature(scope.organizationType, feature) && (feature !== 'online-store' || scope.onlineStoreEnabled !== false), displayName);
}

/** `/shipping` and the zone editor. */
export const RequireShippingZones = requireFeature('shipping-zones', 'RequireShippingZones');
/** `/warehouses`. */
export const RequireWarehouses = requireFeature('warehouses', 'RequireWarehouses');
/** Ecommerce storefront merchandising. */
export const RequireCollections = requireFeature('collections', 'RequireCollections');
/** Ecommerce customer-facing website and setup guide. */
export const RequireOnlineStore = requireFeature('online-store', 'RequireOnlineStore');
/** Restaurant/quotation operational category administration. */
export const RequireOperationalCategories = requireFeature(
  'operational-categories',
  'RequireOperationalCategories',
);

/** Product review management is available to ecommerce managers and owners. */
export const RequireReviews = observer(function RequireReviews() {
  const auth = useAuthStore();
  if (auth.organization?.type !== 'ecommerce' || !can(auth.role, 'reviews.manage')) {
    return <Navigate to="/products" replace />;
  }
  return <Outlet />;
});

/**
 * Register, queue and day session — all `sell`/`shift.open` on the server
 * (pos-api/routes/api.php:126, 178-186), so a waiter meets a 403 at every one.
 *
 * An unknown/absent role still passes: `can()` treats null as allowed so a
 * pre-roles backend behaves exactly as it did before roles existed
 * (07-roles-permissions.md §4), and the server stays the only real boundary.
 */
export const RequireSell = requireEligibility((scope) => can(scope.role, 'sell'), 'RequireSell');

/** Scan-first stock work waits for current server capabilities before rendering. */
export const RequireStockOperations = observer(function RequireStockOperations() {
  const auth = useAuthStore();
  if (!auth.hydrated || !auth.reconciled) return <Booting />;
  const scope = scopeOf(auth);
  if (isRetailMode(auth.organization?.type)) {
    return <Navigate to={landingRouteFor(scope)} replace />;
  }
  if (
    !can(scope.role, 'inventory.receive') &&
    !can(scope.role, 'inventory.count') &&
    !can(scope.role, 'inventory.transfer')
  ) {
    return <Navigate to={landingRouteFor(scope)} replace />;
  }
  return <Outlet />;
});
